// ── AdminNotificationBadge.jsx ─────────────────────────────────────────────────
// مكون يعرض إشعارات الأدمن في الوقت الفعلي من RTDB
// يستمع لـ:
//   /pendingPayments  — إثباتات الدفع الجديدة  (المسار الصحيح من Payment.jsx)
//   /supportTickets   — تذاكر الدعم الجديدة    (المسار الصحيح من Support.jsx)
//   /complaints       — الشكاوي الجديدة         (المسار الصحيح من SendComplaint.jsx)
// يُحدّث شارة العدد فوراً عند ورود بيانات جديدة
// يُرسل إشعار push للأدمن عند أي حدث جديد
// ملاحظة: هذا المكون لا يعرض زر الجرس — الإشعارات تُرسَل فقط في الخلفية

import { useEffect, useRef } from "react";
import { ref, onValue, off } from "firebase/database";
import { db } from "../firebase";
import { showLocalNotification, isNotifTypeEnabled, NOTIF_TYPES } from "../hooks/useFCM";

// ── أنواع الإشعارات الإدارية ──────────────────────────────────────────────────
const ADMIN_NOTIF_CONFIG = {
  payment: {
    titleAr: "إثبات دفع جديد 💰",
    titleEn: "New Payment Proof 💰",
    route:   "/adminpanel/pendingpayments",
    notifType: NOTIF_TYPES.ADMIN_PAYMENT,
    tag:     "admin-payment",
  },
  support: {
    titleAr: "طلب دعم جديد 🎧",
    titleEn: "New Support Request 🎧",
    route:   "/adminpanel/Respondingcomplaints",
    notifType: NOTIF_TYPES.ADMIN_SUPPORT,
    tag:     "admin-support",
  },
  complaint: {
    titleAr: "شكوى جديدة ⚠️",
    titleEn: "New Complaint ⚠️",
    route:   "/adminpanel/Respondingcomplaints",
    notifType: NOTIF_TYPES.ADMIN_COMPLAINT,
    tag:     "admin-complaint",
  },
};

// ── مكون خفي يرسل إشعارات الأدمن في الخلفية ─────────────────────────────────
const AdminNotificationBadge = ({ isAr }) => {
  const prevKeysRef = useRef(new Set()); // تتبع الإشعارات المرسلة مسبقاً

  // ── بناء معرف فريد للإشعار ─────────────────────────────────────────────────
  const makeId = (kind, key) => `${kind}_${key}`;

  // ── إرسال إشعار محلي (إذا كان النوع مفعّلاً في تفضيلات المالك) ────────────
  const sendOwnerNotif = (kind, username) => {
    const cfg = ADMIN_NOTIF_CONFIG[kind];
    if (!cfg) return;
    if (!isNotifTypeEnabled(cfg.notifType)) return;
    // ✅ استخدام كلا اللغتين في showLocalNotification لضمان العرض الصحيح
    const titleToShow = isAr ? cfg.titleAr : cfg.titleEn;
    const bodyToShow  = isAr ? `من: ${username}` : `From: ${username}`;
    showLocalNotification(
      titleToShow || cfg.titleAr || cfg.titleEn || "LeaksMercy",
      bodyToShow  || "",
      {
        tag:       cfg.tag,
        url:       cfg.route,
        notifType: cfg.notifType,
      }
    );
  };

  // ── الاستماع لإثباتات الدفع الجديدة (pendingPayments) ──────────────────────
  useEffect(() => {
    const payRef = ref(db, "pendingPayments");
    const unsub = onValue(payRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.val();
      Object.entries(data).forEach(([key, val]) => {
        if (val.status !== "pending") return;
        const id = makeId("payment", key);
        if (prevKeysRef.current.has(id)) return;
        prevKeysRef.current.add(id);
        const username = val.phone || val.username || "—";
        sendOwnerNotif("payment", username);
      });
    });
    return () => off(payRef);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAr]);

  // ── الاستماع لتذاكر الدعم الجديدة (supportTickets) ─────────────────────────
  useEffect(() => {
    const ticketsRef = ref(db, "supportTickets");
    const unsub = onValue(ticketsRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.val();
      Object.entries(data).forEach(([key, val]) => {
        if (val.status !== "pending") return;
        if (val.adminRead === true) return;
        const id = makeId("support", key);
        if (prevKeysRef.current.has(id)) return;
        prevKeysRef.current.add(id);
        sendOwnerNotif("support", val.username || "—");
      });
    });
    return () => off(ticketsRef);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAr]);

  // ── الاستماع للشكاوي الجديدة (complaints) ──────────────────────────────────
  useEffect(() => {
    const complaintsRef = ref(db, "complaints");
    const unsub = onValue(complaintsRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.val();
      Object.entries(data).forEach(([key, val]) => {
        if (val.status !== "pending") return;
        if (val.adminRead === true) return;
        const id = makeId("complaint", key);
        if (prevKeysRef.current.has(id)) return;
        prevKeysRef.current.add(id);
        sendOwnerNotif("complaint", val.username || "—");
      });
    });
    return () => off(complaintsRef);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAr]);

  // لا يعرض أي UI — يعمل فقط في الخلفية
  return null;
};

export default AdminNotificationBadge;
